import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface DashboardMetrics {
  totalOrders: number;
  ordersThisMonth: number;
  totalInventoryValue: number;
  lowStockVariants: number;
  accountBalance: number;
  activeUsers: number;
  pendingReturns: number;
  revenue: number;
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      // Simulated metrics - in production these would come from actual API calls
      setMetrics({
        totalOrders: 1240,
        ordersThisMonth: 156,
        totalInventoryValue: 45230.50,
        lowStockVariants: 12,
        accountBalance: 125680.00,
        activeUsers: 8,
        pendingReturns: 5,
        revenue: 234560.00,
      });
    } catch (err) {
      console.error('Failed to fetch metrics', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Havenly OS</h1>
            <p className="text-sm text-gray-500">Unified Business Operations Platform</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600">
              {user?.fullName} <span className="text-sm text-gray-500">({user?.role})</span>
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <NavCard
            title="Inventory"
            description="Manage products and stock"
            href="/inventory"
            icon="📦"
            badge={metrics?.lowStockVariants}
          />
          <NavCard
            title="Orders"
            description="Track orders and shipments"
            href="/tracking"
            icon="📍"
            badge={metrics?.ordersThisMonth}
          />
          <NavCard
            title="Finance"
            description="Manage finances and accounts"
            href="/finance"
            icon="💰"
          />
          <NavCard
            title="Reports"
            description="Generate analytics and reports"
            href="/reports"
            icon="📊"
          />
        </div>

        {/* Metrics Grid */}
        {isLoading ? (
          <div className="text-center py-12">Loading metrics...</div>
        ) : metrics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <MetricCard
              title="Total Orders"
              value={metrics.totalOrders.toString()}
              subtitle={`${metrics.ordersThisMonth} this month`}
              icon="📦"
              color="blue"
            />
            <MetricCard
              title="Low Stock Items"
              value={metrics.lowStockVariants.toString()}
              subtitle="Variants below threshold"
              icon="⚠️"
              color="yellow"
            />
            <MetricCard
              title="Account Balance"
              value={`$${metrics.accountBalance.toFixed(2)}`}
              subtitle="Total available funds"
              icon="💳"
              color="green"
            />
            <MetricCard
              title="Pending Returns"
              value={metrics.pendingReturns.toString()}
              subtitle="Awaiting approval"
              icon="↩️"
              color="purple"
            />
            <MetricCard
              title="Monthly Revenue"
              value={`$${metrics.revenue.toFixed(2)}`}
              subtitle="Current period"
              icon="📈"
              color="green"
            />
            <MetricCard
              title="Active Users"
              value={metrics.activeUsers.toString()}
              subtitle="System users"
              icon="👥"
              color="blue"
            />
            <MetricCard
              title="Inventory Value"
              value={`$${metrics.totalInventoryValue.toFixed(2)}`}
              subtitle="Total stock value"
              icon="📊"
              color="indigo"
            />
            <MetricCard
              title="Orders This Month"
              value={metrics.ordersThisMonth.toString()}
              subtitle={`${((metrics.ordersThisMonth / metrics.totalOrders) * 100).toFixed(1)}% of total`}
              icon="📈"
              color="orange"
            />
          </div>
        ) : null}

        {/* Quick Stats */}
        <div className="bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold mb-6">System Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatSection
              title="Operations Health"
              items={[
                { label: 'Stock Alerts', value: 'Low stock on 12 items', color: 'text-yellow-600' },
                { label: 'Pending Tasks', value: '5 returns awaiting review', color: 'text-orange-600' },
                { label: 'System Status', value: 'All systems operational', color: 'text-green-600' },
              ]}
            />
            <StatSection
              title="Financial Snapshot"
              items={[
                { label: 'Monthly Revenue', value: '$234,560.00', color: 'text-green-600' },
                { label: 'Account Balance', value: '$125,680.00', color: 'text-blue-600' },
                { label: 'Inventory Value', value: '$45,230.50', color: 'text-indigo-600' },
              ]}
            />
            <StatSection
              title="Activity Summary"
              items={[
                { label: 'Orders This Month', value: '156 orders', color: 'text-blue-600' },
                { label: 'Active Users', value: '8 users online', color: 'text-green-600' },
                { label: 'Last Activity', value: 'Just now', color: 'text-gray-600' },
              ]}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

function NavCard({
  title,
  description,
  href,
  icon,
  badge,
}: {
  title: string;
  description: string;
  href: string;
  icon: string;
  badge?: number;
}) {
  return (
    <a
      href={href}
      className="bg-white rounded-lg shadow hover:shadow-lg transition p-6 cursor-pointer relative"
    >
      <div className="text-4xl mb-2">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
      {badge && badge > 0 && (
        <div className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
          {badge}
        </div>
      )}
    </a>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  color,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: string;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
    indigo: 'bg-indigo-50 border-indigo-200',
  };

  return (
    <div className={`${colorClasses[color]} border rounded-lg p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          <p className="text-gray-500 text-xs mt-1">{subtitle}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );
}

function StatSection({
  title,
  items,
}: {
  title: string;
  items: Array<{ label: string; value: string; color: string }>;
}) {
  return (
    <div>
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center">
            <span className="text-gray-600">{item.label}</span>
            <span className={`font-semibold ${item.color}`}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
